import { ServerBillInfo } from '@/api/type-server'
import { ItemPaymentType } from '@/consts/order'
import { formatDuration, formatMillicentPrice } from '@/utils/formatter'
import { formatOldMachineName } from '@/utils/node'

import { getBillDurationSec } from './billDuration'

export enum BonusType {
  Bean = 1,
  Time = 2,
}

export enum DiscountType {
  Coupon = 0,
  Time = 1,
  Bean = 2,
}

const SUBSCRIPTION_BILLING_TERM: Partial<Record<ItemPaymentType, string>> = {
  [ItemPaymentType.Available30Days]: 'Monthly',
  [ItemPaymentType.Available7Days]: 'Weekly',
  [ItemPaymentType.Available1Days]: 'Daily',
}

export class MachineBill {
  billType = '-'
  bill: ServerBillInfo
  usedTimeStr = ''
  price = 0
  priceMillicent = 0
  priceStr = ''

  constructor(sourceData: ServerBillInfo) {
    this.bill = sourceData
    this.initNodeBillInfo()
  }

  get displayID() {
    return this.bill.displayID
  }

  get billTime() {
    return this.bill.billTime.seconds * 1000
  }

  get startTime() {
    return this.bill.timeInfo.startTime.seconds * 1000
  }

  get endTime() {
    return (this.bill.timeInfo.endTime?.seconds || 0) * 1000 || Date.now()
  }

  get isRunning() {
    return this.bill.type === 1
  }

  get amount() {
    return this.bill.amount
  }

  get type() {
    return this.bill.type
  }

  get subtype() {
    return this.bill.subType
  }

  get billingInfo() {
    return this.bill.Info.BillingInfo
  }

  get hwItemBillInfo() {
    return this.bill.Info.HwItemBillInfo
  }

  get timeBonusBillInfo() {
    return this.bill.Info.TimeBonusBillInfo
  }

  get hostName() {
    if (this.timeBonusBillInfo) {
      return Object.keys(this.timeBonusBillInfo.bonusConds.conds[0].gpuModelLimit.models).join(',')
    }

    return formatOldMachineName(this.billingInfo?.hardware || this.hwItemBillInfo)
  }

  get sourceType() {
    return this.bill.sourceType
  }

  get typeStr() {
    return (
      BILL_TYPE_SUBTYPE_DISPLAY[`${this.type}-${this.subtype}`] ||
      BILL_SUBTYPE_DISPLAY[this.subtype] ||
      BILL_SOURCE_TYPE_DISPLAY[this.sourceType ?? -1] ||
      BILL_TYPE_DISPLAY[this.type] ||
      '-'
    )
  }

  get billingMethodLabel(): string {
    if (this.hwItemBillInfo) return 'Subscription'
    if (this.billingInfo) return 'Pay-as-you-go'
    return this.typeStr || '-'
  }

  get discountLabel(): string {
    const info = this.billingInfo
    if (!info) return 'No Discount'

    const discounts: string[] = []
    if (info.couponAmountYuan > 0) discounts.push('Coupon')
    if (info.totalBonus.timeSec > 0) discounts.push('Time Bonus')
    if (info.totalBonus.moneyCent > 0) discounts.push('Credit Bonus')

    return discounts.length ? discounts.join(' + ') : 'No Discount'
  }

  initNodeBillInfo() {
    const durationSec = getBillDurationSec(this.bill)

    if (this.billingInfo) {
      this.billType = 'Hourly'
      const { hardware } = this.billingInfo
      this.formatBillInfo({
        durationSec,
        priceMillicent: hardware.discountPriceMillicent,
      })
    } else if (this.hwItemBillInfo) {
      const { paymentType, PriceMillicent, PriceCent } = this.hwItemBillInfo ?? {}
      const resolvedPaymentType = paymentType
      this.billType = resolvedPaymentType
        ? (SUBSCRIPTION_BILLING_TERM[resolvedPaymentType] ?? '')
        : ''

      this.formatBillInfo({
        durationSec,
        priceMillicent: PriceMillicent ?? (PriceCent === undefined ? undefined : PriceCent * 1000),
      })
    }
  }

  formatBillInfo({
    durationSec,
    priceMillicent,
  }: { durationSec?: number; priceMillicent?: number } = {}) {
    this.usedTimeStr = durationSec !== undefined ? formatDuration(durationSec) : '-'
    this.priceMillicent = priceMillicent ?? 0
    this.price = this.priceMillicent / 1000
    this.priceStr = priceMillicent
      ? `${formatMillicentPrice(this.priceMillicent, false)}/hour`
      : '-'
  }
}

export const BILL_SUBTYPE = {
  Invalid: 0, // no sub type
  BillingTransfer: 1, // transfer money from parent account to children account, e.g. oversea account
  PayGroupBonus: 2, // pay bonus for group nember, including user himself
  EduHwBonusPurchase: 3, // edu hw bonus purchase
  OrderToBilling: 4, // mark bill of OrderToBilling
  EduStorageDiscount: 5, // mark for edu storage discount
  TimeLimitedStorageRenew: 6,
  TimeLimitedStorageAutoRenew: 7,
  RechargeByCMB: 8,
  RechargeByAlipay: 9,
  RechargeByCorporateAccounts: 10, // used in MMS/GRPC
  RechargeByTaoBao: 11, // used in MMS/GRPC
  GiftByCooperation: 12, // gift money by cooperation, used in MMS/GRPC
  GiftByActivity: 13, // gift money by activity, used in MMS/GRPC
  GiftForExperience: 14, // experience reward, used in MMS/GRPC
  GiftByInternal: 15, // internal gift, used in MMS/GRPC
  ManualDeduction: 16, // manual deduction, used in MMS/GRPC
  TimeLimitedStorageFirstBuy: 17,
  TimeLimitedStorageModify: 18,
  GroupStorageCreate: 19,
  GroupStorageModify: 20,
  GroupStorageRenew: 21,
  GroupStorageAutoRenew: 22,
  PayCourseBonus: 23, // same info struct with PayGroupBonus, prepare for filter usage
  TimeLimitedMachine: 24,
  TimeLimitedMachineRenew: 25,
  RechargeByPayPal: 26,
  RechargeByNewWebPay: 27,
  RechargeByStripe: 28,
  AssetTransferCredit: 37, // credits transfer to another asset domain (Model Route)
}

/**
 * Subtypes of bills paid with real money, as opposed to gifted/bonus credits.
 * RechargeByCorporateAccounts covers back-office corporate transfers.
 */
export const REAL_RECHARGE_BILL_SUBTYPES: number[] = [
  BILL_SUBTYPE.RechargeByCorporateAccounts,
  BILL_SUBTYPE.RechargeByPayPal,
  BILL_SUBTYPE.RechargeByNewWebPay,
  BILL_SUBTYPE.RechargeByStripe,
]

export const BILL_SUBTYPE_DISPLAY = {
  [BILL_SUBTYPE.BillingTransfer]: 'International Site Credits',
  [BILL_SUBTYPE.PayGroupBonus]: 'Team Computing Power Beans',
  [BILL_SUBTYPE.EduHwBonusPurchase]: 'Student Package',
  [BILL_SUBTYPE.OrderToBilling]: 'Package to Pay-As-You-Go Conversion',
  [BILL_SUBTYPE.TimeLimitedStorageFirstBuy]: 'Datadrive Package',
  [BILL_SUBTYPE.TimeLimitedStorageModify]: 'Datadrive Package Modification',
  [BILL_SUBTYPE.TimeLimitedStorageRenew]: 'Datadrive Package Renewal',
  [BILL_SUBTYPE.TimeLimitedStorageAutoRenew]: 'Datadrive Auto-Renewal',
  [BILL_SUBTYPE.RechargeByCMB]: 'Main Site Recharge',
  [BILL_SUBTYPE.RechargeByAlipay]: 'Main Site Recharge via Alipay',
  [BILL_SUBTYPE.RechargeByCorporateAccounts]: 'Corporate Account Recharge',
  [BILL_SUBTYPE.RechargeByTaoBao]: 'Taobao Recharge',
  [BILL_SUBTYPE.GiftByCooperation]: 'Gift by Partnership',
  [BILL_SUBTYPE.GiftByActivity]: 'Gift by Activity',
  [BILL_SUBTYPE.GiftForExperience]: 'Trial Credits',
  [BILL_SUBTYPE.GiftByInternal]: 'Internal Staff Recharge',
  [BILL_SUBTYPE.ManualDeduction]: 'Manual Deduction',
  [BILL_SUBTYPE.GroupStorageCreate]: 'Team Storage Plan',
  [BILL_SUBTYPE.GroupStorageModify]: 'Team Storage Plan Modification',
  [BILL_SUBTYPE.GroupStorageRenew]: 'Team Storage Plan Renewal',
  [BILL_SUBTYPE.GroupStorageAutoRenew]: 'Team Storage Auto-Renewal',
  [BILL_SUBTYPE.PayCourseBonus]: 'Course Computing Power Beans/Time Card',
  [BILL_SUBTYPE.TimeLimitedMachine]: 'Time-Limited Machine Rental',
  [BILL_SUBTYPE.TimeLimitedMachineRenew]: 'Time-Limited Machine Rental Renewal',
}

/**
 * `${type}-${subType}` names, checked before the subtype/type maps. An asset
 * transfer books subType 37 as 13 hold, then 15 commit or 14 refund; all shown.
 */
export const BILL_TYPE_SUBTYPE_DISPLAY: Record<string, string> = {
  [`13-${BILL_SUBTYPE.AssetTransferCredit}`]: 'Model Route Transfer (Hold)',
  [`14-${BILL_SUBTYPE.AssetTransferCredit}`]: 'Model Route Transfer Refund',
  [`15-${BILL_SUBTYPE.AssetTransferCredit}`]: 'Transfer to Model Route',
}

/** Freeze leg of an asset transfer; released into type 15 or refunded, not an extra charge. */
export function isAssetTransferHold(bill: { type?: number; subType?: number }): boolean {
  return bill.type === 13 && bill.subType === BILL_SUBTYPE.AssetTransferCredit
}

export const BILL_TYPE_DISPLAY: any = {
  1: 'Time-Based Rental',
  2: 'Time-Based Rental',
  3: 'Recharge',
  6: 'Gift',
  7: 'Consumption',
  8: 'Withdrawal',
  10: 'Consumption',
  11: 'Refund',
}

export const BILL_SOURCE_TYPE = {
  Image: 6,
  Snapshot: 7,
} as const

export const BILL_SOURCE_TYPE_DISPLAY: Record<number, string> = {
  [BILL_SOURCE_TYPE.Image]: 'Images Space Rental',
  [BILL_SOURCE_TYPE.Snapshot]: 'Snapshots Space Rental',
}

export const BILL_CURRENCY_DISPLAY = {
  1: 'USD',
  2: 'TWD',
}
